<template>
  <div ref="cart_container" class="cart" v-if="!$noCartMode()">
    <breadcrumbs :breadcrumbs="breadcrumbs" class="container" />

    <div class="cart-header container" :class="{'express-header': cart?.express}">
      <h1>{{ $t('cart') }}</h1>
      <span v-if="cart?.express" class="express">{{ $t('express') }}</span>
      <cart-buttons
        v-if="!!cart"
        @downloadOrderFile="downloadOrderFile"
        @showDeleteDialog="showDeleteDialog = true"
        @showCheckoutDialog="showCheckoutDialog = true"
      />
    </div>

    <template v-if="products.results?.length && !!cart">
      <div class="container">
        <cart-list 
          :cart="cart"
          :products="products.results"
          :stocks="stocks"
          @fetch-cart="fetchCart"
        />
        <div class="results">
          <div class="grid-item result-item">
            <span>{{ $t('total_rub') }}</span>
            <span>
              {{ cart.total_price | integerPart }}
              <span class="small">
                {{ cart.total_price | fractionalPart }}
              </span>
            </span>
          </div>
          <div class="grid-item result-item">
            <span>{{ $t('vat_rub') }}</span>
            <span>{{ cart.total_price * 0.22 | integerPart }}<span class="small">{{ cart.total_price * 0.2| fractionalPart }}</span></span>
          </div>
          <div class="grid-item result-item">
            <span>{{ $t('total_vat_rub') }}</span>
            <span>{{ cart.total_price * 1.22| integerPart }}<span class="small">{{ cart.total_price * 1.2| fractionalPart }}</span></span>
          </div>
          <div class="grid-item result-item" v-for="[propId, propSum] in Object.entries(cart.total_sum_props)">
            <span>{{ getPropertyLocaleName(propId) }}</span>
            <span>{{ propSum }}</span>
          </div>
          <div class="grid-item result-item">
            <span>{{ $t('quantity_pcs') }}</span>
            <span>{{ cart.total_amount | integerPart }}</span>
          </div>
        </div>
      </div>

      <cart-delivery-address
        v-if="$auth.loggedIn && !$auth.user?.scope?.perms?.includes('can_create_order_without_delivery_option')"
        :self-delivery="isSelfDelivery"
        :delivery-address="deliveryAddress"
        :stock="stocks[0]"
        @set-delivery-method="(value) => isSelfDelivery = value"
        @set-delivery-address="(value) => deliveryAddress = value"
      />

      <div v-if="$auth.loggedIn" class="container comment">
        <div class="comment-link">
          <a href="#" @click.prevent="textareaHide = !textareaHide" :aria-label="$t('add_comment')">
            <i class="svg-icon icon-pointer" :class="{'plus': textareaHide, 'minus': !textareaHide}" />
            <h4>{{ $t('add_comment') }}</h4>
            <h3>{{ $t('add_comment') }}</h3>
          </a>
        </div>
        <transition name="fadeHeight" mode="out-in">
          <div class="field comment-text" v-if="!textareaHide"><textarea v-model="comments" /></div>
        </transition>
      </div>

      <div v-if="$auth.loggedIn" class="infoblock">
        <div class="container text-info">
          <i class="svg-icon info" /><span>
            <site-text-block type="cart_info_catalog" class="cart_info_catalog" />
          </span>
        </div>
      </div>

      <div class="container container-buttons">
        <cart-buttons
          @downloadOrderFile="downloadOrderFile"
          @showDeleteDialog="showDeleteDialog = true"
          @showCheckoutDialog="showCheckoutDialog = true"
        />
      </div>
    </template>
    <div class="container" v-else>
      {{ $t('empty_cart') }}
    </div>

    <modal :visible="showDeleteDialog" @close="showDeleteDialog = false">
      <template #header>
        <h3>{{ $t('delete_cart_confirm_1') }}<br>{{ $t('delete_cart_confirm_2') }}</h3>
      </template>
      <template #footer>
        <button class="modal-default-button" @click="deleteCart">{{ $t('clear') }}</button>
      </template>
    </modal>

    <modal :visible="showCheckoutDialog" @close="showCheckoutDialog = false">
      <template #header>
        <h3>{{ $t('checkout_confirm_1') }}<br>{{ $t('checkout_confirm_2') }}</h3>
      </template>
      <template v-if="isRetailer" #body>
        {{ $t('checkout_distributors_list_1') }}
        {{ relatedDistributors.map(x => x.profile?.company_name).join(', ') }}.
        <br>
        {{ $t('checkout_distributors_list_2') }}
        <nuxt-link :to="localePath('/profile/edit/')">{{ $t('checkout_distributors_list_3') }}</nuxt-link>.
      </template>
      <template v-else-if="enabledDiscount" #body>
        <div class="discount_choices">
          <div class="radiobutton">
            <input 
              class="styled-radiobutton"
              id="payment-standard"
              type="radio"
              :checked="selectedDiscount === null"
              @click="selectedDiscount = null"
            />
            <label for="payment-standard" class="radio-label">
              {{ $t('payment_standard') }}
            </label>
            <div class="item-info" />
          </div>
          
          <div class="radiobutton" v-for="discount_item in discounts">
            <input 
              class="styled-radiobutton"
              type="radio"
              :id="`payment-item-${discount_item.id}`"
              :value="discount_item.id"
              :checked="selectedDiscount === discount_item"
              @click="selectedDiscount = discount_item"
            />
            <label :for="`payment-item-${discount_item.id}`" class="radio-label">
              {{ discount_item?.name }}
            </label>
            <div class="item-info" v-html="discount_item?.description" />
          </div>
          
          <div class="discount_total-price">
            <span>{{ $t('sum_rub') }}, {{ $t('no_vat') }}:</span>
            <span />
            <span>{{ cart.total_price | integerPart }}<span class="small">{{ cart.total_price | fractionalPart }}</span></span>
          </div>
          <template v-if="selectedDiscount">
            <div class="discount_total-price">
              <span>{{ $t('discount_rub') }}:</span>
              <span />
              <span>{{ discountSum() | integerPart }}<span class="small">{{ discountSum() | fractionalPart }}</span></span>
            </div>
            <div class="discount_total-price">
              <span>{{ $t('discounted_rub') }}, {{ $t('no_vat') }}:</span>
              <span />
              <span>{{ priceDiscounted() | integerPart }}<span class="small">{{ priceDiscounted() | fractionalPart }}</span></span>
            </div>
          </template>

        </div>
      </template>
      <template #footer>
        <button class="modal-default-button" @click="checkoutCart">{{ $t('submit') }}</button>
      </template>
    </modal>

    <modal :visible="showCheckoutComplete" @close="showCheckoutComplete = false">
      <template #header>
        <h3>{{ $t('checkout_success_header') }}</h3>
      </template>
      <template #body>
        {{ $t(isRetailer ? 'checkout_success_text_1_retailer' : 'checkout_success_text_1') }}<br>
        {{ $t('checkout_success_text_2') }}
      </template>
      <template #footer>
        <button class="modal-default-button" @click="$router.push({path: localePath('/')})">{{ $t('to_homepage') }}</button>
      </template>
    </modal>
  </div>
</template>

<script>
import {rememberExpiresMixin} from '@/mixins';

import { Product, ProductProperty } from '@/models/product';
import { Stock } from '@/models/stock';
import { Discount } from '@/models/discount';
import Breadcrumbs from '@/components/breadcrumbs';
import CartDeliveryAddress from '@/components/cart/cart-delivery-address';
import CartList from '@/components/cart/cart-list';
import CartButtons from '@/components/cart/cart-buttons';
import Modal from '@/components/modal';
import SiteTextBlock from '@/components/blocks/site-text-block';
import { downloadFile } from "@/helpers";

export default {
  name: "CartView",
  components: {
    Breadcrumbs,
    CartDeliveryAddress,
    CartList,
    Modal,
    SiteTextBlock,
    CartButtons,
  },
  mixins: [rememberExpiresMixin],
  data() {
    return {
      products: {},
      productProperties: [],
      stocks: [],
      carts: [],
      comments: null,
      isSelfDelivery: null,
      deliveryAddress: null,
      textareaHide: true,
      moved_products: [],
      breadcrumbs: [],
      showDeleteDialog: false,
      showCheckoutDialog: false,
      showCheckoutComplete: false,
      printWindow: false,
      // showWhereToBuy: true,
      distributors: [],
      discounts: [],
      selectedDiscount: null,
    }
  },
  computed: {
    cart() {
      return this.carts.length ? this.carts[0] : null;
    },
    enabledDiscount(){
      return this.$auth.user?.scope?.perms?.includes('can_edit_discount') &&
      ! this.$auth.user?.scope?.perms?.includes('disable_discount');
    },
    isRetailer() {
      return this.$auth.user?.scope?.perms?.includes('can_access_retailer_account');
    },
    relatedDistributors() {
      return this.distributors.filter(x => this.$auth.user.distributors.map(y => y.distributor).includes(x.id));
    },
  },
  async fetch() {
    [
      this.products,
      this.productProperties,
      this.stocks,
      this.distributors,
      this.discounts
    ] = await Promise.all([
      Product.config({count_loading: true}).params({cart: 1}).first(),
      ProductProperty.config({count_loading: true}).get(),
      Stock.config({count_loading: true}).get(),
      this.$axios.$get('/loyalpro/users/distributors/'),
      Discount.get(),
    ]);
    let response = await this.$axios.$get('/order/user-order/cart/', {count_loading: true});
    this.carts = response.carts;
    this.moved_products = response.moved_products;
  },
  mounted() {
    if(this.$noCartMode()){
      this.$nuxt.context.error({statusCode: 404});
      return
    }
  },
  created() {
    this.breadcrumbs.push({name: this.$t('cart')});
  },
  head() {
    return {
      title: this.$t('cart'),
    }
  },
  methods: {
    priceDiscounted() {
      let price = this.cart.total_price;
      if(this.selectedDiscount){
        price = (100-this.selectedDiscount.discount)/100 * this.cart.total_price;
      }
      price = price.toFixed(2);
      return price;
    },
    discountSum(){
      return (this.cart.total_price - this.priceDiscounted()).toFixed(2);
    },
    closePrintWindow() {
      this.printWindow = false;
    },
    getPropertyLocaleName(propId) {
      let prop = this.productProperties.find(x => x.id === parseInt(propId));
      let data = prop?.property_translations?.find(x => x.language.slug === this.$i18n.locale) || {};
      return data.name || prop?.name;
    },
    checkoutCart() {
      this.showCheckoutDialog = false;
      this.$axios.$post('/order/user-order/checkout/', {
        comment: this.comments,
        self_delivery: this.isSelfDelivery,
        delivery_address: this.deliveryAddress,
        discount_id: this.selectedDiscount?.id || null,
      }).then((response) => {
        this.products = {};
        this.carts = [];
        this.$store.dispatch('setCartExists');
        this.scrollToTop();
        this.showCheckoutComplete = true;
      }).catch((e) => {
        try {
          this.$notify({
            duration: 5000,
            text: e.response.data.detail,
            ignoreDuplicates: true,
          });
        } catch(e) {
        }
      });
    },
    async fetchCart() {
      this.$store.dispatch('addLoading');
      await Promise.all([
        Product.params({cart: 1}).first(),
        this.$axios.$get('/order/user-order/cart/'),
      ]).then((response) => {
        this.products = response[0];
        this.carts = response[1].carts;
        this.moved_products = response[1].moved_products;
      });
      this.$store.dispatch('removeLoading');
    },
    deleteCart() {
      this.$axios.$post('/order/user-order/cart-delete/').then(() => {
        this.products = {};
        this.carts = [];
        this.$store.dispatch('setCartExists');
        this.scrollToTop();
      }).catch((e) => {
        try {
          this.$notify({
            duration: 5000,
            text: e.response.data.detail,
            ignoreDuplicates: true,
          });
        } catch(e) {
        }
      }).finally(() => this.showDeleteDialog = false);
    },
    scrollToTop() {
      this.$refs.cart_container.scrollIntoView({behavior: 'smooth'});
    },
    printCart() {
      window.print();
    },
    downloadOrderFile() {
      downloadFile(this, `/order/user-order/${this.cart.id}/report/`);
    },
  },
}
</script>
